/**
 * Explore stage (§5h) — talk to Claude. The advisor loop (advisorClient.ts) and
 * its latency contract are UNCHANGED; this is the surface: a chat thread of the
 * per-round messages, a plain-English steer box, visible run checkpoints, and a
 * "current best · scored by the engine" side panel with Apply-to-design. Auto-runs
 * to a conclusion; the planner can Stop & keep best at any time.
 *
 * Every figure shown traces to a candidate the engine actually scored — a
 * hallucinated number is structurally impossible (MASTER_PLAN §6.5). Claude's
 * concluding narrative lands in an EDITABLE box and only reaches the PDF once the
 * planner has edited it.
 */
import type { AdvisorRunLog } from './advisorClient'
import { Btn, Callout, Chip } from './ui'

export interface ExploreBest { id: string; contribLabel: string; sub: string }

export interface ExploreChatProps {
  log: AdvisorRunLog | null
  running: boolean
  steer: string
  onSteer: (v: string) => void
  onRun: () => void
  onStop: () => void
  onApply: (candidateId: string) => void
  narrative: string
  onNarrative: (v: string) => void
  best: ExploreBest | null
}

export function ExploreChat({ log, running, steer, onSteer, onRun, onStop, onApply, narrative, onNarrative, best }: ExploreChatProps) {
  const rounds = log?.rounds ?? []
  const rec = log?.recommendation ?? null
  const hasDecision = !!rec?.candidateId
  const hasNarrative = !!(rec?.narrative && rec.narrative.trim())

  const stepState = (done: boolean, now: boolean) => (done ? 'done' : now ? 'now' : 'todo')
  const steps = [
    { label: 'Plan', mark: '1', state: stepState(rounds.length >= 1, running && rounds.length === 0) },
    { label: `Searching (${rounds.length}/8)`, mark: String(Math.max(1, rounds.length)), state: stepState(hasDecision, running && rounds.length >= 1 && !hasDecision) },
    { label: 'Shortlist', mark: '★', state: stepState(hasDecision, !hasDecision && rounds.length >= 2 && running) },
    { label: 'Conclude', mark: '✓', state: stepState(hasNarrative, hasDecision && !hasNarrative) },
  ]

  return (
    <>
      <div className="sh-explore">
        <section className="sh-chat">
          <header className="sh-chat-head">
            <span className="sh-chat-badge">✦</span>
            <span className="sh-chat-title">Claude — Timetable advisor</span>
            <span className="sh-chat-status">
              {running ? <><span className="sh-pulse" /> working · round {rounds.length || 1}/8</> : rec ? 'concluded' : 'ready'}
            </span>
          </header>

          <div className="sh-chat-body">
            {steer.trim() && (
              <div className="sh-msg sh-msg--you">
                <span className="sh-msg-av">CF</span>
                <div className="sh-bubble sh-bubble--you">{steer.trim()}</div>
              </div>
            )}
            {rounds.length === 0 && !running && (
              <div className="sh-msg">
                <span className="sh-msg-av">✦</span>
                <div className="sh-bubble">I&apos;ll score the baseline against a spread of plausible configurations in both modes, then move one thing at a time and pause to conclude. Type a steer if you want to constrain the search (e.g. &ldquo;hold steam at 2 diagrams&rdquo;), then press Run.</div>
              </div>
            )}
            {rounds.map((r) => (
              <div className="sh-msg" key={r.round}>
                <span className="sh-msg-av">✦</span>
                <div className="sh-bubble">
                  <div className="sh-bubble-round">Round {r.round} · {r.results.length} scored</div>
                  {r.message || '(no note)'}
                </div>
              </div>
            ))}
            {rec && (
              <div className="sh-msg">
                <span className="sh-msg-av">✦</span>
                <div className="sh-bubble"><div className="sh-bubble-round">◆ Recommendation</div>{rec.headline}</div>
              </div>
            )}
            {log?.error && (
              <div className="sh-msg">
                <span className="sh-msg-av">✦</span>
                <div className="sh-bubble">{log.error}</div>
              </div>
            )}
          </div>

          <div className="sh-chat-input">
            <input
              type="text" value={steer} placeholder="Steer Claude — e.g. 'hold steam at 2 diagrams and re-search'…"
              disabled={running}
              onChange={(e) => onSteer(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !running) onRun() }}
            />
            {running
              ? <button type="button" className="sh-chat-send sh-chat-send--stop" onClick={onStop}>Stop &amp; keep best</button>
              : <button type="button" className="sh-chat-send" onClick={onRun}>{rounds.length > 0 ? 'Re-run' : 'Run'}</button>}
          </div>
        </section>

        <div className="sh-stack">
          {best && (
            <section className="sh-best">
              <div className="sh-section-label">Current best · scored by the engine</div>
              <div className="sh-best-value">{best.contribLabel}</div>
              <div className="sh-best-sub">{best.sub}</div>
              <button type="button" className="sh-best-apply" disabled={running} onClick={() => onApply(best.id)}>Apply to design</button>
            </section>
          )}
          <section className="sh-best">
            <div className="sh-section-label">Run checkpoints</div>
            <div className="sh-steps">
              {steps.map((s) => (
                <div className="sh-step" key={s.label}>
                  <span className={`sh-step-dot${s.state === 'done' ? ' sh-step-dot--done' : s.state === 'now' ? ' sh-step-dot--now' : ''}`}>{s.state === 'done' ? '✓' : s.mark}</span>
                  <span className={`sh-step-label${s.state === 'now' ? ' sh-step-label--now' : s.state === 'done' ? ' sh-step-label--done' : ''}`}>{s.label}</span>
                </div>
              ))}
            </div>
          </section>
          <Callout kind="info">
            Every figure Claude quotes was computed by <strong>this browser&apos;s production engine</strong>. It searches
            automatically and you can Stop &amp; keep best at any point — a hallucinated number is structurally impossible.
          </Callout>
        </div>
      </div>

      {rec && (
        <div className="sh-mt sh-stack">
          <Callout kind="claude" title={rec.headline}>
            <p style={{ margin: '4px 0' }}>{rec.mechanism}</p>
            {rec.sensitivity && <p style={{ margin: '4px 0' }}><strong>Sensitivity:</strong> {rec.sensitivity}</p>}
            {rec.wouldChangeMind && <p style={{ margin: '4px 0' }}><strong>What would change the recommendation:</strong> {rec.wouldChangeMind}</p>}
            {rec.risks.length > 0 && <ul>{rec.risks.map((rk, i) => <li key={i}>{rk}</li>)}</ul>}
            <div className="sh-mt"><Btn kind="gold" small disabled={running} onClick={() => onApply(rec.candidateId)}>Apply this recommendation to the design</Btn></div>
          </Callout>

          {rec.assumptions.length > 0 && (
            <section className="sh-card">
              <div className="sh-card-head"><span className="sh-card-title">Assumptions Claude used</span></div>
              <div className="stu-scroll">
                <table className="stu-table">
                  <thead><tr><th>Assumption</th><th>Value</th><th>Provenance</th><th>Note</th></tr></thead>
                  <tbody>
                    {rec.assumptions.map((a, i) => (
                      <tr key={i}><td>{a.name}</td><td className="sh-mono">{a.value}</td><td><Chip kind={a.provenance} /></td><td className="stu-note">{a.note}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="sh-card sh-card--pad">
            <div className="sh-section-label">Planner&apos;s notes — kept with this session, never printed in the board paper</div>
            <textarea className="sh-chat-narrative" value={narrative} placeholder="Your own working notes. They save with the session and stay here — the board paper prints computed figures only, never free-form conclusions (Chris, 2026-09-02)." onChange={(e) => onNarrative(e.target.value)} />
          </section>
        </div>
      )}
    </>
  )
}
